import { Box } from "@mui/material";
import { Color } from "types/colors";

const DeliveryMethodIds = {
  Video: "e5b467c4-3385-4419-befe-1645086af7a5",
  Pdf: "e870ccf4-3041-49fa-b68f-afb8c7247f8c",
  ArticulateRise: "82648929-caa3-46be-9843-8480ad5441c0",
  InfographEd: "b47c8b2a-9f1e-4d3a-8c5e-7f2b1a6d9e3c",
  ThreePlayMedia: "4bd04d20-bffa-477a-9a72-812637430d54",
  ISpring: "a6c720a3-e27f-44e5-93cb-fbf87bb5b36c",
  Vimeo: "8f5a2b1c-3d4e-4f6a-7b8c-9d0e1f2a3b4c",
  Page: "f8a3d7c4-9b2e-4f6a-8c1d-5e7b9f3a2d4c",
  Blubrry: "35d5bfdc-070e-4130-b9d0-d8325c1ebbec",
  Copient: "114e0415-aed1-4240-8490-cd7e997ecad0",
  AnatomyTv: "784d41c0-26c1-4c44-8691-9ead953ce2a0",
};

type ContentRendererProps = {
  deliveryMethodId: string | undefined;
  content: string | undefined;
  displayText: string | undefined;
  backgroundColor: string | undefined;
  width: string | undefined;
  height: string | undefined;
};

const useStyles = (backgroundColor: string) => ({
  roundedBox: {
    borderRadius: 1,
    backgroundColor,
    py: 2,
    px: 3,
  },
  video: {
    width: "100%",
    maxHeight: "70vh",
  },
  wideIframe: {
    width: "100%",
    aspectRatio: "16/9",
    border: "none",
  },
  fullHeightIframe: {
    width: "100%",
    height: "70vh",
    border: "none",
  },
  htmlContentBox: {
    maxHeight: "70vh",
    overflow: "auto",
  },
  embeddedIframeBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& > *": {
      width: "100%",
      textAlign: "center",
    },
  },
  linkBox: {
    py: 4,
    textAlign: "center",
  },
});

const ContentRenderer = ({
  deliveryMethodId,
  content,
  displayText,
  backgroundColor,
  width,
  height,
}: ContentRendererProps) => {
  const styles = useStyles(backgroundColor || Color.White);
  const normalizedId = deliveryMethodId?.toLowerCase();

  if (!content) {
    return null;
  }

  if (normalizedId === DeliveryMethodIds.Video.toLowerCase()) {
    return (
      <Box sx={styles.roundedBox}>
        <video
          controls
          controlsList="nodownload"
          src={content}
          style={styles.video}
        />
      </Box>
    );
  }

  if (normalizedId === DeliveryMethodIds.Pdf.toLowerCase()) {
    return (
      <Box sx={styles.roundedBox}>
        <iframe
          title={displayText || "PDF Preview"}
          src={content}
          style={styles.fullHeightIframe}
        />
      </Box>
    );
  }

  if (normalizedId === DeliveryMethodIds.ArticulateRise.toLowerCase()) {
    return (
      <Box sx={styles.roundedBox}>
        <iframe
          title={displayText || "Articulate Rise Preview"}
          src={content}
          style={styles.fullHeightIframe}
        />
      </Box>
    );
  }

  if (
    normalizedId === DeliveryMethodIds.ISpring.toLowerCase() ||
    normalizedId === DeliveryMethodIds.InfographEd.toLowerCase() ||
    normalizedId === DeliveryMethodIds.Vimeo.toLowerCase()
  ) {
    return (
      <Box sx={styles.roundedBox}>
        <iframe
          title={displayText || "Content Preview"}
          src={content}
          style={styles.wideIframe}
          allowFullScreen
        />
      </Box>
    );
  }

  if (
    normalizedId === DeliveryMethodIds.ThreePlayMedia.toLowerCase() ||
    normalizedId === DeliveryMethodIds.Blubrry.toLowerCase() ||
    normalizedId === DeliveryMethodIds.AnatomyTv.toLowerCase()
  ) {
    return (
      <Box
        sx={{
          ...styles.roundedBox,
          ...styles.embeddedIframeBox,
          // The saved markup carries no size and no border, and an iframe without either
          // collapses to 300x150 inside a 2px inset - so the preview draws it at the size the
          // delivery method declares, which is what the learner will get.
          "& iframe": {
            border: 0,
            width: width || "100%",
            maxWidth: "100%",
            height: height,
          },
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </Box>
    );
  }

  if (normalizedId === DeliveryMethodIds.Page.toLowerCase()) {
    return (
      <Box sx={{ ...styles.roundedBox, ...styles.htmlContentBox }}>
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </Box>
    );
  }

  if (normalizedId === DeliveryMethodIds.Copient.toLowerCase()) {
    return (
      <Box sx={styles.roundedBox}>
        <iframe
          title={displayText || "Copient Preview"}
          src={content}
          style={styles.fullHeightIframe}
          referrerPolicy="no-referrer"
          allowFullScreen
        />
      </Box>
    );
  }

  return (
    <Box sx={{ ...styles.roundedBox, ...styles.linkBox }}>
      <a href={content} target="_blank" rel="noopener noreferrer">
        {displayText || "Open content in new tab"}
      </a>
    </Box>
  );
};

export default ContentRenderer;
